import {
  getPowerUpMultiplier,
  getPowerUpBonusPercent,
  MAX_POWER_UP_MULTIPLIER,
} from './powerUp';
import { CORE_MASSIVE_ATTACK_BONUS } from './coreDamage';
import { TRAP_VARIANTS } from './trapScene';

const SQUARES = [
  ...Array(1).fill({ modifier: 20 }),
  ...Array(2).fill({ modifier: 25 }),
  { modifier: 30 },
  { modifier: 0, effect: { type: 'heal', amount: 3 } },
  ...Array(2).fill({ modifier: -25 }),
  { modifier: -20 },
  ...Array(2).fill({ modifier: 0, effect: { type: 'critical' } }),
  ...Array(2).fill({ modifier: 0, effect: { type: 'criticalMiss' } }),
  { modifier: 0, effect: { type: 'heal', amount: 2 } },
  ...Array(4).fill({ modifier: 0, effect: { type: 'shield', amount: 2 } }),
  { modifier: 0, effect: { type: 'relic' } },
  { modifier: 20 },
];

const TRAP_SUMMARY =
  'Freeze this turn: your attack is canceled. A surviving enemy attacks twice before your next throw.';

export const BOARD_TILE_ABILITIES = {
  trap: {
    name: 'Trap',
    color: '#9bcaff',
    short: 'TRAP',
    mini: 'FROZEN',
    summary: TRAP_SUMMARY,
    description: `${TRAP_SUMMARY} Multiple traps in one throw do not extend the freeze. Sector 1 has no enemy to attack you.`,
  },
  relay: {
    name: 'Relay',
    color: '#71f5df',
    short: 'RELAY',
    description: 'Choose an adjacent tile to activate instead.',
  },
  gambit: {
    name: 'Gambit',
    color: '#ffbc76',
    short: 'GAMBIT',
    mini: 'ROLL',
    description:
      'Keep the current result, or spend 1 shard to roll your selected die again. It launches to a different tile, and the new result replaces the old one. One use per throw.',
  },
  powerUp: {
    name: 'Power Up',
    color: '#ffe079',
    short: 'POWER UP',
    description:
      'Boost every die face by 20%, keeping each face’s own number, then blast the die back into the air. Each activation raises this tile by one level, up to Power Up ×10 (+200%). Chain Reaction applies a neighboring Power Up as a roll bonus without relaunching; only a direct landing launches the die.',
  },
  interference: {
    name: 'Interference',
    color: '#79dfff',
    short: 'WEAKEN',
    mini: '−1 HP',
    coreOnly: true,
    description:
      'The Core’s attacks are 1 damage weaker for this round. Multiple activations stack.',
  },
  sabotage: {
    name: 'Sabotage',
    color: '#ff86b4',
    short: 'SABOTAGE',
    mini: 'SABO',
    coreOnly: true,
    description:
      'Corrupt the Core’s next board: Critical Hits become zero-damage Misses, and positive HP damage modifiers become negative.',
  },
  recharge: {
    name: 'Recharge',
    color: '#94baff',
    short: 'RECHARGE',
    mini: 'RESET',
    coreOnly: true,
    description:
      'Restore a spent Phase Anchor. Chain Reaction cannot be recharged.',
  },
};

export const getBoardTileAbility = (effect) => {
  const ability = BOARD_TILE_ABILITIES[effect?.type];
  if (effect?.type !== 'powerUp') return ability;
  const multiplier = getPowerUpMultiplier(effect);
  if (multiplier === 1) return ability;
  const nextMultiplier = Math.min(MAX_POWER_UP_MULTIPLIER, multiplier + 1);
  return {
    ...ability,
    name: `Power Up ×${multiplier}`,
    short: `POWER UP ×${multiplier}`,
    description: `Boost every die face’s own number by ${getPowerUpBonusPercent(
      multiplier
    )}% and blast it back into the air. ${
      multiplier < MAX_POWER_UP_MULTIPLIER
        ? `This tile rebuilds as Power Up ×${nextMultiplier} (+${getPowerUpBonusPercent(
            nextMultiplier
          )}%).`
        : `This tile stays at Power Up ×${MAX_POWER_UP_MULTIPLIER} (+${getPowerUpBonusPercent(
            MAX_POWER_UP_MULTIPLIER
          )}%).`
    } It can be activated again. Chain Reaction applies a neighboring Power Up as a roll bonus without relaunching.`,
  };
};

export const DOOR_BOARD_VERSION = 18;
export const BREACH_BOARD_VERSION = 7;
export const BOSS_BOARD_VERSION = 6;

// One growth pickup per sector; its spent hazard persists between throws.
export const withSingleGrowthTile = (source) => {
  if (source.some((square) => square.spentGrowth)) return source;
  let found = false;
  const board = source.map((square) => {
    if (
      square.coreDamage ||
      square.effect?.type !== 'heal' ||
      square.effect.amount !== 2
    )
      return square;
    if (!found) {
      found = true;
      return square;
    }
    return { ...square, effect: { ...square.effect, amount: 1 } };
  });
  if (!found) {
    const index = board.findIndex(
      (square, index) =>
        ![0, 4, 20, 24].includes(index) &&
        !square.coreDamage &&
        (square.effect?.type === 'heal' || !square.effect)
    );
    if (index >= 0)
      board[index] = { modifier: 0, effect: { type: 'heal', amount: 2 } };
  }
  return board;
};

export const spendGrowthTile = (square) =>
  !square.coreDamage &&
  square.effect?.type === 'heal' &&
  square.effect.amount === 2
    ? { modifier: 0, effect: { type: 'damage', amount: 3 }, spentGrowth: true }
    : square;

// Keep the corner hazards and utility upgrades. Only sectors 1, 5 and the
// Core retain a chest; other sectors trade that chest for another trap.
export const withPlayerBoardTraps = (
  source,
  { floor = 2, core = false, breach = false } = {}
) => {
  const keepChest = core || [1, 5, 10].includes(floor);
  let chests = 0;
  let numericReplacements = 0;
  const trap = () => ({ modifier: 0, effect: { type: 'trap' } });
  const board = source.map((square) => {
    if (square.coreDamage) return square;
    if (square.effect?.type === 'relic') {
      chests += 1;
      return keepChest && chests === 1 ? square : trap();
    }
    if (!square.effect && square.modifier > 0 && numericReplacements < 2) {
      numericReplacements += 1;
      return trap();
    }
    return square;
  });
  let count = board.filter((square) => square.effect?.type === 'trap').length;
  for (const index of [
    ...board.map((square, index) =>
      !square.effect && square.modifier < 0 ? index : -1
    ),
    ...board.map((square, index) => (!square.effect ? index : -1)),
    ...board.map((square, index) => (square.effect ? index : -1)),
  ].filter((index) => index >= 0)) {
    if (count >= 3) break;
    if ([0, 4, 20, 24].includes(index)) continue;
    const square = board[index];
    const replaceable = breach
      ? square.effect?.type === 'heal'
      : !square.effect ||
        ['shards', 'heal', 'shield'].includes(square.effect.type);
    if (!square.coreDamage && replaceable) {
      board[index] = trap();
      count += 1;
    }
  }
  return withSingleGrowthTile(
    board.map((square, index) =>
      square.effect?.type === 'trap' && !square.effect.variant
        ? {
            ...square,
            effect: { ...square.effect, variant: TRAP_VARIANTS[index % 3] },
          }
        : square
    )
  );
};

// Upgrade between throws. Relocate helpful corner tiles into the interior;
// preserve upgraded Power Ups and never rewrite an in-progress landing.
export const migratePlayerBoardBalance = (game) => {
  if (
    !game ||
    !(game.breachIntro
      ? [3, 4, 5, 6].includes(game.doorBoardVersion)
      : [13, 14, 15, 16, 17].includes(game.doorBoardVersion)) ||
    !['ready', 'guardianReady', 'bossReady', 'minibossReady'].includes(
      game.phase
    ) ||
    !Array.isArray(game.doorBoard) ||
    game.doorBoard.length !== 25
  )
    return game;
  return {
    ...game,
    doorBoardVersion: game.breachIntro
      ? BREACH_BOARD_VERSION
      : DOOR_BOARD_VERSION,
    doorBoard: withPlayerBoardTraps(
      withPlayerBoardCorners(
        game.doorBoard.map((square) => {
          const effect = square.effect;
          if (game.doorBoardVersion !== 13 || square.coreDamage || !effect)
            return square;
          const amount =
            effect.type === 'shield' && effect.amount === 3
              ? 2
              : effect.type === 'heal' && effect.amount === 5
              ? 3
              : effect.amount;
          return amount === effect.amount
            ? square
            : { ...square, effect: { ...effect, amount } };
        })
      ),
      {
        floor: game.floor,
        core: game.phase === 'bossReady',
        breach: game.breachIntro,
      }
    ),
  };
};

// Upgrade retired tiles in place so a saved throw keeps its landing index.
export const replaceRetiredBossTile = (square) =>
  square?.effect?.type === 'skull'
    ? { modifier: -2, coreDamage: true }
    : square;

const BOSS_SQUARES = [
  { modifier: 1 },
  { modifier: 1 },
  { modifier: 1 },
  { modifier: 1 },
  { modifier: 2 },
  { modifier: 2 },
  { modifier: -1 },
  { modifier: -1 },
  { modifier: -1 },
  { modifier: -1 },
  { modifier: -2 },
  { modifier: -2 },
  ...Array(2).fill({ modifier: 0, effect: { type: 'critical' } }),
  ...Array(2).fill({ modifier: 0, effect: { type: 'criticalMiss' } }),
  ...Array(2).fill({ modifier: 0, effect: { type: 'critical' } }),
  ...Array(2).fill({ modifier: 0, effect: { type: 'shield', amount: 20 } }),
  ...Array(2).fill({ modifier: 0, effect: { type: 'shield', amount: 30 } }),
  { modifier: -2 },
  ...Array(2).fill({ modifier: 0, effect: { type: 'massiveAttack' } }),
].map((square) => ({ ...square, coreDamage: true }));

const shuffleSquares = (source, random) => {
  const squares = [...source];
  const board = [];
  while (squares.length) {
    const index = Math.min(
      squares.length - 1,
      Math.floor(random() * squares.length)
    );
    board.push({ ...squares.splice(index, 1)[0] });
  }
  return board;
};

// North is row zero, away from the sling. Move existing hazards into the
// corners so useful tiles (including utility upgrades) keep a place inside.
export const withPlayerBoardCorners = (source) => {
  if (!Array.isArray(source) || source.length !== 25) return source;
  const board = [...source];
  const fixed = new Set();
  const reserve = (target, matches, replacement) => {
    let origin = matches(board[target])
      ? target
      : board.findIndex(
          (square, index) => !fixed.has(index) && matches(square)
        );
    if (origin < 0)
      origin = board.findIndex(
        (square, index) =>
          !fixed.has(index) &&
          square.effect?.type === 'shards' &&
          square.effect.amount === 1
      );
    if (origin < 0)
      origin = board.findIndex(
        (square, index) =>
          !fixed.has(index) &&
          (!square.effect || ['shards', 'heal'].includes(square.effect.type))
      );
    if (origin >= 0 && origin !== target)
      [board[target], board[origin]] = [board[origin], board[target]];
    board[target] = replacement;
    fixed.add(target);
  };
  [0, 4].forEach((index) =>
    reserve(index, (square) => square.effect?.type === 'criticalMiss', {
      modifier: 0,
      effect: { type: 'criticalMiss' },
    })
  );
  [20, 24].forEach((index) =>
    reserve(index, (square) => !square.effect && square.modifier < 0, {
      modifier: -50,
    })
  );
  return board;
};

export const generateDoorBoard = (
  random = Math.random,
  { core = false, floor = 2 } = {}
) => {
  const general = ['relay', 'gambit', 'powerUp', 'powerUp', 'powerUp'];
  const coreTiles = ['interference', 'sabotage', 'recharge'];
  // Six former numeric/healing cells become utility slots. Core boards always
  // have all three Core abilities and rotate three of the general abilities.
  const types = core
    ? [
        ...shuffleSquares(
          general.map((type) => ({ type })),
          random
        )
          .slice(0, 3)
          .map(({ type }) => type),
        ...coreTiles,
      ]
    : [...general, general[Math.min(4, Math.floor(random() * general.length))]];
  return withPlayerBoardTraps(
    withPlayerBoardCorners(
      shuffleSquares(
        [
          ...SQUARES,
          ...types.map((type) => ({ modifier: 0, effect: { type } })),
        ],
        random
      )
    ),
    { core, floor }
  );
};
export const generateBossDoorBoard = (random = Math.random) =>
  shuffleSquares(BOSS_SQUARES, random);

// The intro board teaches currency, healing, and relic rewards.
const breachEffectSquares = (type, amount, count) =>
  Array.from({ length: count }, () => ({
    modifier: 0,
    effect: { type, amount },
  }));

export const generateBreachBoard = (random = Math.random) =>
  withPlayerBoardTraps(
    withPlayerBoardCorners(
      shuffleSquares(
        [
          ...breachEffectSquares('shards', 1, 4),
          ...breachEffectSquares('shards', 2, 8),
          ...breachEffectSquares('shards', 3, 3),
          ...breachEffectSquares('shards', 5, 1),
          ...breachEffectSquares('heal', 1, 5),
          ...breachEffectSquares('heal', 2, 3),
          ...Array.from({ length: 1 }, () => ({
            modifier: 0,
            effect: { type: 'relic' },
          })),
        ],
        random
      )
    ),
    { floor: 1, breach: true }
  );

export const getBoardSquare = (board, index) =>
  Array.isArray(board) && Number.isInteger(index) && index >= 0
    ? board[index] || null
    : null;

export const applyBoardSquareRoll = (value, square) => {
  square = replaceRetiredBossTile(square);
  if (!square?.coreDamage && square?.effect?.type === 'trap') return 0;
  if (square?.coreDamage) {
    if (square.effect?.type === 'criticalMiss') return 0;
    if (square.effect?.type === 'critical') return value * 2;
    if (square.effect?.type === 'massiveAttack')
      return value + CORE_MASSIVE_ATTACK_BONUS;
    if (square.effect?.type === 'corePower')
      return Math.max(
        0,
        Math.ceil(
          (value + (square.modifier || 0)) * (1 + 0.2 * square.effect.level)
        )
      );
    return Math.max(0, value + (square.modifier || 0));
  }
  if (square?.effect?.type === 'critical') return value * 2;
  if (square?.effect?.type === 'criticalMiss')
    return Math.round(value / 3) + (square.modifier || 0);
  if (square?.operation === 'multiply')
    return value * square.value + (square.modifier || 0);
  if (square?.operation === 'divide')
    return Math.floor(value / square.value) + (square.modifier || 0);
  return value + (square?.modifier || 0);
};

export const getBoardSquareImpactLabel = (square) => {
  square = replaceRetiredBossTile(square);
  if (!square) return '—';
  if (square.coreDamage) {
    if (square.effect?.type === 'corePower')
      return `${square.modifier > 0 ? '+' : ''}${
        square.modifier || 0
      } · CORE POWER +${square.effect.level * 20}%`;
    if (square.effect?.type === 'criticalMiss') return 'MISS';
    if (square.effect?.type === 'critical') return 'CRITICAL HIT';
    if (square.effect?.type === 'massiveAttack') return 'MASSIVE ATTACK';
    if (square.effect?.type === 'shield')
      return `${square.effect.amount} DAMAGE BLOCK`;
    if (!square.effect)
      return `${square.modifier > 0 ? '+' : ''}${square.modifier}`;
  }
  if (square.effect?.type === 'corruption') return 'CORRUPTED −100';
  const ability = getBoardTileAbility(square.effect);
  if (ability) return ability.name.toUpperCase();
  if (square.effect?.type === 'critical') return 'CRITICAL HIT';
  if (square.effect?.type === 'criticalMiss') return 'CRITICAL MISS';
  if (square.effect?.type === 'relic') return 'TREASURE CHEST';
  if (square.effect?.type === 'shards')
    return `+${square.effect.amount} SHARD${
      square.effect.amount === 1 ? '' : 'S'
    }`;
  if (square.effect?.type === 'heal') return `+ ${square.effect.amount} Max HP`;
  if (square.effect?.type === 'damage') return `−${square.effect.amount} HP`;
  if (square.effect?.type === 'shield')
    return `${square.effect.amount} DAMAGE BLOCK`;
  if (square.effect?.type === 'massiveAttack') return 'MASSIVE ATTACK';
  if (square.operation === 'multiply') return `×${square.value}`;
  if (square.operation === 'divide') return `÷${square.value}`;
  return square.modifier > 0 ? `+${square.modifier}` : `${square.modifier}`;
};

export const getBoardSquareLabel = (square) => {
  square = replaceRetiredBossTile(square);
  if (!square) return '—';
  if (square.coreDamage) return getBoardSquareImpactLabel(square);
  if (square.effect?.type === 'critical') return 'CRITICAL HIT';
  if (square.effect?.type === 'criticalMiss') return 'CRITICAL MISS';
  const modifier = getBoardSquareImpactLabel(square);
  if (square.effect?.type === 'relic') return 'TREASURE CHEST';
  if (square.effect?.type === 'heal')
    return square.modifier
      ? `${modifier} · + ${square.effect.amount} Max HP`
      : `+ ${square.effect.amount} Max HP`;
  if (square.effect?.type === 'shards')
    return square.modifier
      ? `${square.modifier > 0 ? '+' : ''}${square.modifier} · ${modifier}`
      : modifier;
  if (square.effect?.type === 'damage')
    return square.modifier
      ? `${square.modifier > 0 ? '+' : ''}${square.modifier} · ${modifier}`
      : modifier;
  return modifier;
};

export const getBoardSquareColor = (square) => {
  if (square?.effect?.type === 'corruption') return '#ca79ff';
  if (square?.effect?.type === 'corePower') return '#ffbd58';
  if (BOARD_TILE_ABILITIES[square?.effect?.type])
    return BOARD_TILE_ABILITIES[square.effect.type].color;
  if (square?.effect?.type === 'critical') return '#ff78dd';
  if (square?.effect?.type === 'criticalMiss') return '#ff8b9e';
  if (square?.effect?.type === 'relic') return '#ffd978';
  if (square?.effect?.type === 'heal') return '#8dffb1';
  if (square?.effect?.type === 'shield') return '#75eaff';
  if (square?.effect?.type === 'massiveAttack') return '#ff2344';
  if (square?.effect?.type === 'shards') return '#ffe17b';
  if (
    square?.effect?.type === 'damage' ||
    square?.operation === 'divide' ||
    square?.modifier < 0
  )
    return '#ff8b9e';
  if (square?.operation === 'multiply' || square?.modifier > 0)
    return '#80eeff';
  return '#bbc9e2';
};

export const isNegativeBoardSquare = (square) =>
  square?.effect?.type === 'trap' ||
  square?.effect?.type === 'corruption' ||
  square?.effect?.type === 'damage' ||
  square?.effect?.type === 'massiveAttack' ||
  square?.effect?.type === 'criticalMiss' ||
  square?.operation === 'divide' ||
  square?.modifier < 0;

export const getBoardSquareBackgroundColor = (square) =>
  square?.effect?.type === 'corruption'
    ? '#37104f'
    : square?.effect?.type === 'corePower'
    ? '#493117'
    : square?.effect?.type === 'massiveAttack'
    ? '#ff2344'
    : isNegativeBoardSquare(square)
    ? '#470914'
    : getBoardSquareColor(square);

export const getBoardSquareLandingBackgroundColor = (square) =>
  square?.effect?.type === 'critical'
    ? '#f9e6f5'
    : square?.effect?.type === 'criticalMiss' || square?.operation === 'divide'
    ? '#f3f5fa'
    : getBoardSquareBackgroundColor(square);

export const getBoardSquareTextColor = (square) => {
  if (square?.effect?.type === 'corruption') return '#000000';
  if (square?.effect?.type === 'corePower') return getBoardSquareColor(square);
  if (square?.effect?.type === 'massiveAttack') return '#fff4f0';
  if (BOARD_TILE_ABILITIES[square?.effect?.type])
    return BOARD_TILE_ABILITIES[square.effect.type].color;
  if (square?.effect?.type === 'critical') return '#ff78dd';
  if (square?.effect?.type === 'criticalMiss') return '#ff8b9e';
  if (square?.effect?.type === 'relic') return '#ffd978';
  if (square?.effect?.type === 'shards') return '#ffe17b';
  if (square?.effect?.type === 'shield') return '#35e8ff';
  if (isNegativeBoardSquare(square)) return '#43f4ff';
  if (
    square?.effect?.type === 'heal' ||
    square?.effect?.type === 'shield' ||
    square?.effect?.type === 'shards' ||
    square?.operation === 'multiply' ||
    square?.modifier > 0
  )
    return '#35e879';
  return '#bbc9e2';
};

export const getBoardSquareLandingTextColor = (square) => {
  if (BOARD_TILE_ABILITIES[square?.effect?.type]) return '#08121f';
  if (square?.effect?.type === 'critical') return '#35102f';
  if (square?.effect?.type === 'criticalMiss') return '#10131b';
  if (square?.effect?.type === 'shield') return '#06151b';
  if (square?.operation === 'divide') return '#10131b';
  if (square?.effect?.type === 'relic' || isNegativeBoardSquare(square))
    return getBoardSquareTextColor(square);
  if (
    square?.effect?.type === 'heal' ||
    square?.effect?.type === 'shards' ||
    square?.operation === 'multiply' ||
    square?.modifier > 0
  )
    return '#050609';
  return getBoardSquareTextColor(square);
};
